import Phaser from 'phaser';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { PATCH_NOTES, PATCH_KIND_INFO, type PatchVersion, type PatchEntryKind } from '../data/patchNotes';
import { GAME_W, GAME_H, GAME_CX } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';
import { addHoverPulse } from '../ui/ButtonPolish';
import { GAME_VERSION } from '../systems/MetaManager';
import { markPatchNotesAsSeen } from '../systems/PatchNotesTracker';

// === NOTES DE MISE À JOUR ===
// Affiche l'historique des changements en langage joueur.
// Source : src/data/patchNotes.ts (curaté à la main pour rester lisible).
// Ajouté en Phase 10 (v0.39.0-beta).

const HEADER_H = 70;

export class PatchNotesScene extends Phaser.Scene {
  private panel!: ScrollablePanel;
  private returnScene = 'BootScene';
  private returnData: Record<string, unknown> = {};

  constructor() {
    super({ key: 'PatchNotesScene' });
  }

  init(data: { returnTo?: string } = {}): void {
    this.returnScene = data.returnTo ?? 'BootScene';
    this.returnData = {};
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    // Marquer les notes comme vues : prochaine ouverture du jeu = pas de popup
    markPatchNotesAsSeen();
    this.drawUI();
  }

  private drawUI(): void {
    this.children.removeAll(true);
    this.add.rectangle(GAME_CX, GAME_H / 2, GAME_W, GAME_H, 0x0a0a1e).setDepth(0);

    // Header
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);
    this.add.text(GAME_CX, 14, '📋 Notes de mise à jour', {
      fontSize: '18px', color: '#3498db', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);
    this.add.text(GAME_CX, 38, `Version actuelle : v${GAME_VERSION}`, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(11);

    // Bouton retour
    const backBtn = this.add.text(15, 12, '← Retour', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 10, y: 6 },
    }).setDepth(12);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => startSceneWithFade(this, this.returnScene, this.returnData));
    backBtn.on('pointerover', () => backBtn.setColor('#ff6b6b'));
    backBtn.on('pointerout', () => backBtn.setColor('#e74c3c'));
    addHoverPulse(this, backBtn);

    // Séparateur
    this.add.rectangle(GAME_CX, HEADER_H, GAME_W, 1, 0x2a3a5c, 0.6).setOrigin(0.5).setDepth(10);

    // Contenu scrollable
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H);

    let y = 12;
    for (const version of PATCH_NOTES) {
      y = this.drawVersion(version, y);
    }

    y += 8;
    const footnote = this.add.text(GAME_CX, y,
      'Les versions plus anciennes sont consultables sur le dépôt du jeu.', {
      fontSize: '10px', color: '#5a6470', fontFamily: FONT_BODY,
      align: 'center', fontStyle: 'italic',
    }).setOrigin(0.5, 0).setDepth(1);
    this.panel.add(footnote);
    y += footnote.height + 24;

    this.panel.setContentHeight(y);
  }

  private drawVersion(version: PatchVersion, y: number): number {
    const cardW = Math.min(560, GAME_W - 20);
    const lx = GAME_CX - cardW / 2 + 16;
    const wrapW = cardW - 32;
    const isCurrent = version.version === GAME_VERSION;

    // Titre version + date
    const titleColor = isCurrent ? '#f1c40f' : '#ecf0f1';
    const titleText = isCurrent
      ? `v${version.version}  •  ${version.date}  ⭐ Version actuelle`
      : `v${version.version}  •  ${version.date}`;
    const title = this.add.text(lx, y + 10, titleText, {
      fontSize: '13px', color: titleColor, fontFamily: FONT_BODY, fontStyle: 'bold',
      wordWrap: { width: wrapW },
    });

    // Headline (s'il existe)
    let nextY = y + 10 + title.height + 4;
    let headlineObj: Phaser.GameObjects.Text | null = null;
    if (version.headline) {
      headlineObj = this.add.text(lx, nextY, version.headline, {
        fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY, fontStyle: 'italic',
        wordWrap: { width: wrapW },
      });
      nextY += headlineObj.height + 6;
    }

    // Grouper les entries par catégorie pour une lecture plus naturelle
    const groups: Record<PatchEntryKind, string[]> = { new: [], improvement: [], fix: [] };
    for (const e of version.entries) {
      groups[e.kind].push(e.text);
    }

    const entryObjects: Phaser.GameObjects.Text[] = [];
    const ORDER: PatchEntryKind[] = ['new', 'improvement', 'fix'];
    for (const kind of ORDER) {
      const items = groups[kind];
      if (items.length === 0) continue;
      const info = PATCH_KIND_INFO[kind];
      // Sous-titre catégorie
      const catLabel = this.add.text(lx, nextY, `${info.emoji} ${info.label}`, {
        fontSize: '10px', color: info.color, fontFamily: FONT_BODY, fontStyle: 'bold',
      });
      entryObjects.push(catLabel);
      nextY += catLabel.height + 2;
      // Bullets
      for (const item of items) {
        const bullet = this.add.text(lx + 12, nextY, `• ${item}`, {
          fontSize: '10px', color: '#bdc3c7', fontFamily: FONT_BODY,
          wordWrap: { width: wrapW - 12 },
        });
        entryObjects.push(bullet);
        nextY += bullet.height + 3;
      }
      nextY += 4;
    }

    const cardH = Math.max(70, nextY - y + 8);
    const bgColor = isCurrent ? 0x2a2010 : 0x1a1a2e;
    const borderColor = isCurrent ? 0xf1c40f : 0x2a3a5c;
    const bg = this.add.rectangle(GAME_CX, y + cardH / 2, cardW, cardH, bgColor)
      .setStrokeStyle(isCurrent ? 2 : 1, borderColor, 0.85);
    bg.setDepth(0);

    this.panel.add(bg);
    this.panel.add(title);
    if (headlineObj) this.panel.add(headlineObj);
    title.setDepth(1);
    headlineObj?.setDepth(1);
    entryObjects.forEach(t => {
      this.panel.add(t);
      t.setDepth(1);
    });

    return y + cardH + 10;
  }

  shutdown(): void {
    this.panel?.destroy();
  }
}
